/**
 * Grounded synthesis, and the gate that makes it usable.
 *
 * **Five endpoints shipped at M5 with no screen.** They return 503 when no model is
 * configured, which is most installations, so the first job of this panel is to say that
 * plainly rather than render a broken button.
 *
 * Three properties this screen must not soften, all of them ADR-0017:
 *
 * 1. **Every displayed claim carries its citations.** A generated sentence with no route
 *    back to an assertion is exactly what this platform exists not to produce. The
 *    citations are links, not footnote markers.
 * 2. **Stripped claims are reported, not hidden.** The generator produced them and the
 *    citation check removed them for want of support. A panel that showed only what
 *    survived would present a filtered view as the whole answer, and the strip rate is the
 *    single most useful signal about whether the output can be trusted.
 * 3. **Generated text is labelled as generated.** It is a reading of the evidence, not a
 *    finding. Nothing here is promoted into the case's claims, and the wording says so.
 *
 * **The history outlives the binding.** Generation needs a model; *reading what was already
 * generated* does not. This panel used to return the "no model configured" card and stop,
 * which would have hidden every past invocation on any deployment that unbound its provider
 * — and the invocation record is the audit trail for text a person may have acted on.
 */

import { useEffect, useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ApiError, request } from '@/lib/api';
import type { AiModel, AiProvider, GenerationAccepted, Invocation } from '@/lib/schemas';
import { Badge, Button, Card, ErrorState, Loading, Table } from '@/components/ui';
import { useCan } from '@/hooks/usePermissions';

/** The AI synthesis tab for one case. */
export function AiTab({ caseId }: { caseId: string }): ReactElement {
  const canGenerate = useCan('generate_reports');

  const models = useQuery({
    queryKey: ['ai-models'],
    queryFn: () => request<AiModel[]>('/ai/models'),
    // A 503 here is the expected state on an install with no model, not an error worth
    // retrying at somebody.
    retry: false,
  });

  // **Queued, not generated here.** The endpoints return 202 and the `ai` worker does the
  // work, so what a click produces is a promise that an invocation will appear rather than
  // the invocation itself. Generation takes one to three minutes against a local model;
  // holding the request open for that was the previous design and it made the browser wait.
  const [awaiting, setAwaiting] = useState<{ task: string; since: number } | null>(null);
  const [resultId, setResultId] = useState<string | null>(null);

  const queued = (task: string) => () => {
    setResultId(null);
    setAwaiting({ task, since: Date.now() });
  };

  const summarise = useMutation({
    mutationFn: () =>
      request<GenerationAccepted>(`/cases/${caseId}/ai/summarise`, { method: 'POST' }),
    onSuccess: queued('case_summary'),
  });
  const risk = useMutation({
    mutationFn: () =>
      request<GenerationAccepted>(`/cases/${caseId}/ai/risk-analysis`, { method: 'POST' }),
    onSuccess: queued('risk_analysis'),
  });

  // Poll only while something is outstanding. A generation that never lands would
  // otherwise poll for the life of the tab.
  const pendingWatch = useQuery({
    queryKey: ['ai-invocations', caseId],
    queryFn: () =>
      request<Invocation[]>(`/cases/${caseId}/ai/invocations`, { query: { limit: 10 } }),
    enabled: awaiting !== null,
    refetchInterval: awaiting !== null ? 3000 : false,
  });

  useEffect(() => {
    if (awaiting === null) return;
    const landed = (pendingWatch.data ?? []).find(
      (item) =>
        item.task_key === awaiting.task && Date.parse(item.created_at) >= awaiting.since - 5000,
    );
    if (landed) {
      setResultId(landed.id);
      setAwaiting(null);
    }
  }, [awaiting, pendingWatch.data]);

  if (models.isLoading) return <Loading />;

  const unavailable =
    models.error instanceof ApiError || (models.data ?? []).length === 0;

  const pending = summarise.isPending || risk.isPending || awaiting !== null;
  const failure = summarise.error ?? risk.error;

  // Not an early return. What has already been generated is readable whether or not a
  // model is bound today, and the record is the audit trail for text somebody may have
  // acted on months ago.
  if (unavailable) {
    return (
      <div className="space-y-4">
        <Card title="Synthesis is not available">
          <div className="space-y-2 p-4 text-sm">
            <Badge tone="muted">No model configured</Badge>
            <p className="text-muted">
              This deployment has no language model bound, so summaries and risk analysis
              cannot be generated. Everything else about the case works normally — synthesis
              is an optional reading of evidence that already exists, never a source of it.
            </p>
            <p className="text-xs text-muted">
              An operator configures a provider through{' '}
              <span className="font-mono">PRP_AI_*</span> and restarts the API;{' '}
              <span className="font-mono">docs/PRODUCTION_DEPLOYMENT.md</span> covers the two
              supported postures.
            </p>
          </div>
        </Card>
        <InvocationHistory caseId={caseId} />
        <Routing />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Card title="Generate a reading of this case">
        <div className="space-y-3 p-4">
          <p className="text-xs text-muted">
            A model reads the claims this case has already collected and writes them up.
            <strong> It adds no information.</strong> Every sentence it produces must cite
            the assertions it rests on, and any sentence that cannot is removed before you
            see it.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              data-testid="ai-summarise"
              tone="primary"
              disabled={!canGenerate || pending}
              onClick={() => summarise.mutate()}
            >
              {pending ? 'Queued…' : 'Summarise'}
            </Button>
            <Button
              data-testid="ai-risk"
              disabled={!canGenerate || pending}
              onClick={() => risk.mutate()}
            >
              {pending ? 'Queued…' : 'Risk analysis'}
            </Button>
          </div>
          {!canGenerate && (
            <p className="text-xs text-muted">
              Generating requires the <span className="font-mono">generate_reports</span>{' '}
              permission.
            </p>
          )}
          {failure != null && <ErrorState error={failure} />}
        </div>
      </Card>

      {awaiting !== null && (
        <Card title="Queued">
          <p className="p-4 text-sm text-muted" data-testid="ai-queued">
            The <span className="font-mono">{awaiting.task}</span> generation is running on
            the AI worker. A local model takes one to three minutes; this panel fills in
            when it lands, and the result is recorded whether or not this tab is still open.
          </p>
        </Card>
      )}

      {resultId !== null && <SynthesisResult caseId={caseId} invocationId={resultId} />}

      <InvocationHistory caseId={caseId} />
      <Routing />
    </div>
  );
}

/**
 * One synthesis, with its citations and its strip rate.
 *
 * @param props - The case and the synthesis to render.
 * @returns The result panel.
 */
function SynthesisResult({
  caseId,
  invocationId,
}: {
  caseId: string;
  invocationId: string;
}): ReactElement {
  // Read from the stored record rather than from a response body. The claims are
  // persisted now (`ai_claim`), which is what lets a summary survive the tab that asked
  // for it — and is the only way a worker's output can be shown at all.
  const record = useQuery({
    queryKey: ['ai-invocation', invocationId],
    queryFn: () => request<Invocation>(`/ai/invocations/${invocationId}`),
  });

  if (record.isLoading) return <Loading label="Reading the generated summary" />;
  if (record.error) return <ErrorState error={record.error} />;
  if (!record.data) return <p className="text-sm text-muted">That generation is not readable.</p>;

  const result = record.data;
  const stripped = result.claims_stripped;
  const reasons = Object.entries(result.stripped_reasons ?? {});

  return (
    <Card title="Generated — not a finding">
      <div className="space-y-4 p-4">
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <Badge tone="warn">generated text</Badge>
          <span className="font-mono text-muted">
            {result.provider}/{result.model} · {result.prompt_template_version}
          </span>
          <Badge tone={stripped > 0 ? 'warn' : 'ok'} >
            {result.claims_generated} written, {stripped} removed
          </Badge>
        </div>

        {/* The gate, stated rather than hidden. A panel showing only surviving claims
            would present a filtered view as the whole answer. */}
        {stripped > 0 && (
          <div data-testid="ai-stripped" className="rounded border border-line bg-base p-3">
            <p className="text-xs">
              <strong>{stripped}</strong> generated sentence{stripped === 1 ? ' was' : 's were'}{' '}
              removed for want of support in the evidence. That is the citation check
              working, and a high proportion is a reason to distrust what remains.
            </p>
            {reasons.length > 0 && (
              <ul className="mt-1 space-y-0.5">
                {reasons.map(([reason, count]) => (
                  <li key={reason} className="font-mono text-xs text-muted">
                    {reason}: {String(count)}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {result.claims.length === 0 ? (
          <p className="text-sm text-muted">
            Nothing survived the citation check. There is not enough corroborated material
            in this case for a grounded summary yet.
          </p>
        ) : (
          <ol className="space-y-3" data-testid="ai-claims">
            {result.claims.map((claim, index) => (
              <li key={`${claim.section}-${index}`} className="space-y-1">
                <div className="text-xs uppercase tracking-wide text-muted">
                  {claim.section}
                </div>
                <p className="text-sm">{claim.text}</p>
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="text-muted">Cites</span>
                  {claim.cited_assertion_ids.map((assertionId) => (
                    // The route back to the evidence. A generated sentence whose citation
                    // could not be followed would be the thing this platform exists not to
                    // produce.
                    <Link
                      key={assertionId}
                      data-testid="ai-citation"
                      to={`/cases/${caseId}/evidence?assertion=${assertionId}`}
                      className="font-mono text-accent"
                    >
                      {assertionId.slice(0, 8)}
                    </Link>
                  ))}
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>
    </Card>
  );
}

/**
 * How an invocation ended, rendered so that `quarantined` cannot be mistaken for a fault.
 *
 * **This is the reason the history has a screen at all.** A quarantine is not a failure of
 * the platform: something in the *evidence* tried to issue instructions to the model, so
 * nothing was generated. A client that showed it as a generic error would hide the one
 * thing an operator needs to see (`API_GUIDE.md` §5.5), and it would hide it precisely
 * when a source is behaving adversarially.
 *
 * @param props - The status string as the API reports it.
 * @returns The badge.
 */
export function StatusBadge({ status }: { status: string }): ReactElement {
  const tone = status === 'succeeded' ? 'ok' : status === 'quarantined' ? 'warn' : 'danger';
  // `Badge` forwards no `data-*`, so the test id goes on a wrapper.
  return (
    <span data-testid={`invocation-status-${status}`}>
      <Badge tone={tone}>{status}</Badge>
    </span>
  );
}

/**
 * Every generation this case has seen, most recent first.
 *
 * Listed whether or not a model is bound today. Five AI endpoints shipped at M5 and this
 * record had no screen at all, so "what did the model actually say, and when" was a
 * question only a database shell could answer.
 *
 * @param props - The case.
 * @returns The history panel.
 */
function InvocationHistory({ caseId }: { caseId: string }): ReactElement {
  const [selected, setSelected] = useState<string | null>(null);

  const invocations = useQuery({
    queryKey: ['ai-invocations', caseId],
    queryFn: () =>
      request<Invocation[]>(`/cases/${caseId}/ai/invocations`, { query: { limit: 50 } }),
  });

  if (invocations.isLoading) return <Loading label="Reading the generation history" />;
  if (invocations.error) return <ErrorState error={invocations.error} />;

  const rows = invocations.data ?? [];
  const quarantined = rows.filter((item) => item.status === 'quarantined').length;

  return (
    <Card title="Generation history">
      <div className="space-y-3 p-4">
        {rows.length === 0 ? (
          <p className="text-sm text-muted">
            Nothing has been generated for this case. The record exists from the first
            summary onwards, and it is written whether the generation succeeded or not.
          </p>
        ) : (
          <>
            {quarantined > 0 && (
              <div
                data-testid="quarantine-notice"
                className="rounded border border-warn/40 bg-warn/10 p-3 text-xs"
              >
                <strong>
                  {quarantined} generation{quarantined === 1 ? '' : 's'} quarantined.
                </strong>{' '}
                Something in the collected evidence tried to issue instructions to the model,
                so nothing was generated and the attempt was recorded instead. This is a
                finding about a source, not a platform fault — the source that produced that
                evidence is worth looking at.
              </div>
            )}

            <Table
              headers={['When', 'Task', 'Status', 'Model', 'Written / removed', '']}
              empty="No generations."
              rows={rows.map((item) => [
                <span key="t" className="text-xs text-muted">
                  {item.created_at.slice(0, 19).replace('T', ' ')}
                </span>,
                <span key="k" className="font-mono text-xs">
                  {item.task_key}
                </span>,
                <StatusBadge key="s" status={item.status} />,
                <span key="m" className="font-mono text-xs text-muted">
                  {item.provider}/{item.model}
                </span>,
                // The strip rate, not just the counts: it is the metric worth alerting on,
                // and a high proportion is a reason to distrust what survived.
                <span key="c" className="text-xs" data-testid="invocation-strip">
                  {item.claims_generated} / {item.claims_stripped}
                  {item.claims_generated > 0 && (
                    <span className="ml-2 text-muted">
                      {Math.round(item.strip_rate * 100)}% removed
                    </span>
                  )}
                </span>,
                <button
                  key="o"
                  type="button"
                  data-testid="open-invocation"
                  onClick={() => setSelected(selected === item.id ? null : item.id)}
                  className="text-xs text-accent"
                >
                  {selected === item.id ? 'Hide' : 'Record →'}
                </button>,
              ])}
            />

            {selected !== null && <InvocationRecord invocationId={selected} />}
          </>
        )}
      </div>
    </Card>
  );
}

/**
 * The full audit record of one generation.
 *
 * Fetched separately rather than expanded from the list row, because this is the record an
 * auditor reads and it should come from the endpoint that serves exactly it.
 *
 * @param props - Which invocation.
 * @returns The record.
 */
function InvocationRecord({ invocationId }: { invocationId: string }): ReactElement {
  const record = useQuery({
    queryKey: ['ai-invocation', invocationId],
    queryFn: () => request<Invocation>(`/ai/invocations/${invocationId}`),
  });

  if (record.isLoading) return <Loading label="Reading the record" />;
  if (record.error) return <ErrorState error={record.error} />;
  if (!record.data) return <p className="text-sm text-muted">That record no longer exists.</p>;

  const item = record.data;
  const reasons = Object.entries(item.stripped_reasons ?? {});

  return (
    <div className="space-y-3 rounded border border-line bg-base p-3" data-testid="invocation-record">
      {/* **The reason a generation produced nothing, on the screen built to explain it.**
          This record used to show the audit fields and stop, so "why did that fail" was
          answerable only from a worker log — three separate synthesis defects in this
          deployment were diagnosed that way, by somebody with database access. The
          verbatim error is still content-addressed in the evidence store; this is the
          sentence a person reads. */}
      {item.failure_detail != null && item.failure_detail !== '' && (
        <div data-testid="invocation-failure" className="rounded border border-danger/40 bg-danger/10 p-3">
          <p className="text-xs">
            <strong>Nothing was generated.</strong> {item.failure_detail}
          </p>
        </div>
      )}

      {item.status === 'quarantined' && (
        <p className="text-xs">
          <strong>Quarantined.</strong> The bundle assembled for this generation contained
          something that read as an instruction to the model. Nothing was generated, and the
          attempt is recorded here rather than discarded — a prompt-injection attempt in
          collected material is itself a finding.
        </p>
      )}

      <dl className="grid gap-3 text-sm sm:grid-cols-2">
        <Field label="Task">
          <span className="font-mono text-xs">{item.task_key}</span>
        </Field>
        <Field label="Prompt template">
          <span className="font-mono text-xs">{item.prompt_template_version}</span>
        </Field>
        <Field label="Model">
          <span className="font-mono text-xs">
            {item.provider}/{item.model}
          </span>
        </Field>
        <Field label="Latency">
          <span className="text-xs">{item.latency_ms} ms</span>
        </Field>
        <Field label="Tokens">
          <span className="text-xs">
            {item.input_tokens ?? '—'} in / {item.output_tokens ?? '—'} out
            {(item.input_tokens === null || item.output_tokens === null) && (
              <span className="ml-2 text-muted">not reported by the provider</span>
            )}
          </span>
        </Field>
        <Field label="Run">
          <span className="font-mono text-xs">
            {item.run_id === null ? 'generated by hand' : item.run_id.slice(0, 8)}
          </span>
        </Field>
      </dl>

      <div>
        <div className="text-xs uppercase tracking-wide text-muted">Parameters</div>
        <ul className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5">
          {Object.entries(item.parameters ?? {}).map(([key, value]) => (
            <li key={key} className="font-mono text-xs text-muted">
              {key}: {String(value)}
            </li>
          ))}
        </ul>
      </div>

      <div>
        <div className="text-xs uppercase tracking-wide text-muted">Content addresses</div>
        <dl className="mt-1 space-y-0.5">
          <Digest label="Bundle shown" value={item.input_evidence_sha256} />
          <Digest label="Prompt sent" value={item.request_evidence_sha256} />
          <Digest label="Raw response" value={item.response_evidence_sha256} />
        </dl>
        {/* Deliberately not links. These bodies are written straight to the evidence store
            and get no `evidence_blob` row, so `/evidence/{sha}/content` does not resolve
            them — an anchor here would be a 404 dressed up as a download, which is a bug
            this codebase has already shipped once. They are recorded so a stored prompt can
            be verified against the record by whoever holds the store. */}
        <p className="mt-1 text-xs text-muted">
          Recorded so a stored prompt or response can be checked against this record. They
          are held in the evidence store and are not served by the evidence endpoints, which
          index only collected responses.
        </p>
      </div>

      {reasons.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-wide text-muted">Why claims were removed</div>
          <ul className="mt-1 space-y-0.5" data-testid="invocation-strip-reasons">
            {reasons.map(([reason, count]) => (
              <li key={reason} className="font-mono text-xs text-muted">
                {reason}: {String(count)}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * One labelled value.
 *
 * @param props - The label and its content.
 * @returns The pair.
 */
function Field({ label, children }: { label: string; children: ReactElement }): ReactElement {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-muted">{label}</dt>
      <dd className="mt-0.5">{children}</dd>
    </div>
  );
}

/**
 * One content address, wrapped so a 64-character digest does not break the layout.
 *
 * @param props - The label and the digest.
 * @returns The pair.
 */
function Digest({ label, value }: { label: string; value: string }): ReactElement {
  return (
    <div className="flex flex-wrap gap-2 text-xs">
      <dt className="text-muted">{label}</dt>
      <dd className="break-all font-mono">{value}</dd>
    </div>
  );
}

/**
 * Which provider runs which task, and under which prompt version.
 *
 * Routing is configuration rather than a case's business, but it is what turns "why did
 * this generation use that model" into a question with an answer on the screen. It reads
 * no provider and generates nothing — it composes prompts locally to see where each task
 * would go.
 *
 * @returns The routing panel.
 */
function Routing(): ReactElement {
  const providers = useQuery({
    queryKey: ['ai-providers'],
    queryFn: () => request<AiProvider[]>('/ai/providers'),
    retry: false,
  });

  return (
    <Card title="Routing">
      <div className="space-y-2 p-4">
        {providers.isLoading && <Loading label="Reading the routing table" />}
        {providers.error != null && <ErrorState error={providers.error} />}
        {providers.data?.length === 0 && (
          <p className="text-sm text-muted">No provider is configured for this workspace.</p>
        )}
        {providers.data?.map((provider) => (
          <div key={provider.name} data-testid="ai-provider" className="text-sm">
            <span className="font-mono">{provider.name}</span>
            <ul className="mt-0.5 space-y-0.5">
              {Object.entries(provider.tasks).map(([task, version]) => (
                <li key={task} className="font-mono text-xs text-muted">
                  {task} → prompt {version}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </Card>
  );
}
